import useSidebarState from '../../hooks/useSidebarState';
import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Sidebar from '../Sidebar';
import FeatureGate from '../Trial/FeatureGate';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import useCompanyUID from '../../hooks/useCompanyUID';
import useTeamAccessProfile from '../../hooks/useTeamAccessProfile';
import ExecutiveOverview from './subviews/AnalyticsOverview/ExecutiveOverview';
import Recruitment from './subviews/Recruitment';
import PipelineRisk from './subviews/PipelineRisk';
import QualityOfHires from './subviews/QualityOfHires';
import RecruiterPerformance from './subviews/RecruiterPerformance';
import ReportBuilder from './subviews/ReportBuilder';
import './DataAnalytics.css';

const SECTION_OPTIONS = [
  {
    id: 'executive-overview',
    label: { en: 'Executive Overview', ar: 'نظرة تنفيذية عامة' }
  },
  {
    id: 'recruitment',
    label: { en: 'Recruitment', ar: 'التوظيف' }
  },
  {
    id: 'pipeline-risk',
    label: { en: 'Pipeline & Risk', ar: 'مسار التوظيف والمخاطر' }
  },
  {
    id: 'quality-of-hires',
    label: { en: 'Quality of Hires Beta', ar: 'جودة التعيينات - تجريبي' }
  },
  {
    id: 'recruiter-performance',
    label: { en: 'Recruiter Performance', ar: 'أداء مسؤولي التوظيف' }
  },
  {
    id: 'report-builder',
    label: { en: 'Report Builder', ar: 'منشئ التقارير' }
  }
];

const DataAnalytics = () => {
  useUiLanguage();
  const { currentUser } = useAuth();
  const { companyUID, loading: companyUidLoading } = useCompanyUID();
  const { isRTL } = useLanguage();
  const teamAccess = useTeamAccessProfile(companyUID);
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarState();
  const [activeSection, setActiveSection] = useState('executive-overview');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const activeOption = useMemo(
    () => SECTION_OPTIONS.find((option) => option.id === activeSection) || SECTION_OPTIONS[0],
    [activeSection]
  );

  const activeView = useMemo(() => {
    switch (activeSection) {
      case 'recruitment':
        return <Recruitment isRTL={isRTL} companyUID={companyUID} analyticsActorId={currentUser?.uid || 'system'} />;
      case 'pipeline-risk':
        return <PipelineRisk isRTL={isRTL} companyUID={companyUID} analyticsActorId={currentUser?.uid || 'system'} />;
      case 'quality-of-hires':
        return <QualityOfHires isRTL={isRTL} companyUID={companyUID} analyticsActorId={currentUser?.uid || 'system'} />;
      case 'recruiter-performance':
        return <RecruiterPerformance isRTL={isRTL} companyUID={companyUID} analyticsActorId={currentUser?.uid || 'system'} />;
      case 'report-builder':
        return <ReportBuilder isRTL={isRTL} companyUID={companyUID} analyticsActorId={currentUser?.uid || 'system'} />;
      case 'executive-overview':
      default:
        return <ExecutiveOverview isRTL={isRTL} companyUID={companyUID} analyticsActorId={currentUser?.uid || 'system'} />;
    }
  }, [activeSection, companyUID, currentUser?.uid, isRTL]);

  const canAccessAnalytics = teamAccess.isCompanyOwner || teamAccess.userPermissions.canAccessAnalytics;
  const accessLoading = companyUidLoading || teamAccess.loading;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--app-background, #031128)' }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />

      <div
        className={`data-analytics-page${isRTL ? ' rtl' : ''}`}
        dir={isRTL ? 'rtl' : 'ltr'}
        style={{ '--sidebar-w': sidebarCollapsed ? '60px' : '240px' }}
      >
        <FeatureGate showBanner={false}>
          {accessLoading ? (
            <div className="data-analytics-shell">
              <div className="data-analytics-loading-screen">
                <i className="fa-solid fa-spinner fa-spin" />
                <p>{uiText("Please Wait")}</p>
              </div>
            </div>
          ) : !canAccessAnalytics ? (
            <div className="data-analytics-shell">
              <div className="data-analytics-empty-state">
                <h2>{uiText("Analytics access is restricted")}</h2>
                <p>{uiText("Your team profile does not currently include analytics access. A workspace administrator needs to enable analytics for your account from the team access controls.")}</p>
              </div>
            </div>
          ) : (
            <div className="data-analytics-shell">
              <div className="data-analytics-toolbar" data-tutorial="analytics-toolbar">
                {/* <button
                  type="button"
                  className="data-analytics-export-btn"
                  onClick={() => window.print()}
                  title={isRTL ? 'تصدير التقرير' : 'Export Report'}
                >
                  <i className="fa-solid fa-file-arrow-down" />
                  <span>{isRTL ? 'تصدير التقرير' : 'Export Report'}</span>
                </button> */}

                <div
                  ref={dropdownRef}
                  className="data-analytics-selector"
                  data-tutorial="analytics-view-selector"
                >
                  <button
                    type="button"
                    className="data-analytics-selector-button"
                    aria-expanded={isDropdownOpen}
                    onClick={() => setIsDropdownOpen((prev) => !prev)}
                  >
                    <span className="data-analytics-selector-label">
                      {isRTL ? activeOption.label.ar : activeOption.label.en}
                    </span>
                    <i className="fa-solid fa-circle-chevron-down data-analytics-selector-icon" />
                  </button>

                  <div
                    className="data-analytics-selector-menu"
                    style={{ display: isDropdownOpen ? 'block' : 'none' }}
                  >
                    {SECTION_OPTIONS.map((option) => (
                      <div key={option.id} className="data-analytics-selector-item">
                        <button
                          type="button"
                          className={`data-analytics-selector-option${
                            option.id === activeSection ? ' selected' : ''
                          }`}
                          onClick={() => {
                            setActiveSection(option.id);
                            setIsDropdownOpen(false);
                          }}
                        >
                          {isRTL ? option.label.ar : option.label.en}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="data-analytics-subview">{activeView}</div>
            </div>
          )}
        </FeatureGate>
      </div>
    </div>
  );
};

export default DataAnalytics;
